import React, { useEffect, useState, useContext, useMemo } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";

// ✅ MUST match backend mount
const API_BASE = "https://nissi-india.com/api/ownDetailsEntry";

const OwnVehicleDetailsEntry = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // ✅ storage values
  const finYearId = localStorage.getItem("finYearId") || "";
  const finYear = localStorage.getItem("finYear") || "";

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchType =
    localStorage.getItem("branchType") ||
    localStorage.getItem("branchtype") ||
    "HO";

  const branchId = Number(
    localStorage.getItem("branchId") ||
      localStorage.getItem("branchid") ||
      0
  );

  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": loginUserId,
    }),
    [finYearId, finYear, branchId, branchType, loginUserId]
  );

  // edit mode params
  const vehicleId = Number(searchParams.get("vehicleId") || 0);
  const spareId =
    Number(searchParams.get("spareId") || searchParams.get("spareCatId") || 0);

  const [formType, setFormType] = useState("own"); // own | spares
  const [loading, setLoading] = useState(false);

  const [ddl, setDdl] = useState({
    vehicleBranches: [],
    vehicleBasis: [],
    vehicleEquipmentTypes: [],
    vehicleSubTypes: [],
    vehicleConditions: [],
    utilisations: [],
    fuelTypes: [],
    stores: [],
    locations: [],

    vehMainTypes: [],
    sparesItems: [],
    sparesSuppliers: [],
    sparesModels: [],
    sparesMakes: [],
    sparesLocations: [],
  });

  const [own, setOwn] = useState({
    entryDate: "",

    vehicleBranchId: "",
    storeId: "",
    locationId: "",

    jobcardNo: "",
    hypothecationWith: "",

    vehicleTypeId: "",
    vehicleSubTypeId: "",
    vehicleBasisId: "",

    vehicleCode: "",
    vehicleName: "",
    vehicleMake: "",
    vehicleRegNo: "",
    vehicleRegDate: "",
    model: "",
    vehicleUsageCost: "",
    vehicleDescription: "",
    vehicleConditionId: "",
    utilisationId: "",
    remarks: "",
    fuelTypeId: "",

    mainPowerCost: "",
    fuelTankCapacity: "",

    invoiceNo: "",
    chassisNo: "",
    engineSLNo: "",
    equipmentSLNo: "",
    purchasedFrom: "",
    purchasedDate: "",
    purchasedAmount: "",

    insuranceNo: "",
    insuranceDate: "",
    insuranceAmount: "",
    insuranceRenewalDate: "",
    insurancePremiumPerYear: "",
    insuranceCompany: "",

    fcDate: "",
    fcRenewalDate: "",

    roadTaxFromDate: "",
    roadTaxToDate: "",
    permitDate: "",
    permitExpiryDate: "",
    rcBookNo: "",
    localPermitFromDate: "",
    localPermitExpireDate: "",
    trPermitFromDate: "",
    trPermitExpireDate: "",
    freeServiceDate: "",

    averageValue: "",
    averageType: "",

    hubFittingKm: "1",
    hubFittingHr: "1",

    cumHmr: "",
    lastHmr: "",
    cumKms: "",
    lastKms: "",
    adjHmr: "",
    adjKms: "",
  });

  const [docs, setDocs] = useState({
    insurance: null,
    fitness: null,
    roadTax: null,
    permit: null,
    rcCopy: null,
    invoice: null,
    endorsement: null,
  });

  const [spares, setSpares] = useState({
    vehicleMainTypeId: "",
    vehicleCode: "",

    makeId: "",
    makeName: "",
    modelId: "",
    modelName: "",
    itemId: "",
    itemName: "",
    supplierId: "",
    supplierName: "",

    slNo: "",
    purchaseDate: "",
    expiryDate: "",
    purchaseValue: "",
    locationId: "",
    remarks: "",

    uploadDoc: null,

    isNewMake: false,
    isNewModel: false,
    isNewItem: false,
    isNewSupplier: false,
  });

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const sectionClass = `mb-6 p-6 rounded-2xl shadow-xl border ${
    isDark
      ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
      : `bg-gray-100 ${currentSkin.glass} text-black`
  }`;

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const toISODateInput = (val) => {
    if (!val) return "";
    if (/^\d{4}-\d{2}-\d{2}$/.test(val)) return val;

    const m1 = String(val).match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (m1) {
      const dd = m1[1];
      const mm = m1[2];
      const yyyy = m1[3];
      return `${yyyy}-${mm}-${dd}`;
    }
    return "";
  };

  const handleOwnChange = (e) => {
    const { name, value } = e.target;
    setOwn((prev) => ({ ...prev, [name]: value }));
  };

  const handleSparesChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setSpares((prev) => ({ ...prev, [name]: checked }));
      return;
    }
    setSpares((prev) => ({ ...prev, [name]: value }));
  };

  const handleDocChange = (key) => (e) => {
    const file = e.target.files?.[0] || null;
    setDocs((prev) => ({ ...prev, [key]: file }));
  };

  const handleSparesDocChange = (e) => {
    const file = e.target.files?.[0] || null;
    setSpares((prev) => ({ ...prev, uploadDoc: file }));
  };

  const uploadSingle = async (docType, file) => {
    if (!file) return "";
    const fd = new FormData();
    fd.append("docType", docType);
    fd.append("file", file);

    const res = await axios.post(`${API_BASE}/upload`, fd, {
      headers: {
        ...commonHeaders,
        "Content-Type": "multipart/form-data",
      },
    });
    return res.data?.filename || "";
  };

  const fetchDropdowns = async (branchForStores = branchId) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: {
          branchId: branchForStores,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      const data = res.data || {};

      setDdl({
        vehicleBranches: data.vehicleBranches || [],
        vehicleBasis: data.vehicleBasis || [],
        vehicleEquipmentTypes: data.vehicleEquipmentTypes || [],
        vehicleSubTypes: data.vehicleSubTypes || [],
        vehicleConditions: data.vehicleConditions || [],
        utilisations: data.utilisations || [],
        fuelTypes: data.fuelTypes || [],
        stores: data.stores || [],
        locations: data.locations || [],

        vehMainTypes: data.vehMainTypes || [],
        sparesItems: data.sparesItems || [],
        sparesSuppliers: data.sparesSuppliers || [],
        sparesModels: data.sparesModels || [],
        sparesMakes: data.sparesMakes || [],
        sparesLocations: data.sparesLocations || [],
      });

      setOwn((p) => {
        if (p.entryDate) return p;
        const today = new Date();
        const yyyy = today.getFullYear();
        const mm = String(today.getMonth() + 1).padStart(2, "0");
        const dd = String(today.getDate()).padStart(2, "0");
        return { ...p, entryDate: `${yyyy}-${mm}-${dd}` };
      });

      setOwn((p) => {
        if (branchType !== "HO" && branchId && !p.vehicleBranchId) {
          return { ...p, vehicleBranchId: String(branchId) };
        }
        return p;
      });
    } catch (err) {
      console.error(err);
      toast.error("Failed to load dropdowns");
    } finally {
      setLoading(false);
    }
  };

  const fetchSubtypesByType = async (vehTypeId) => {
    try {
      if (!vehTypeId) return;
      const res = await axios.get(`${API_BASE}/subtypes`, {
        params: {
          equipmentTypeId: vehTypeId,
          branchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });

      const list = Array.isArray(res.data)
        ? res.data
        : res.data?.vehicleSubTypes || [];
      setDdl((p) => ({ ...p, vehicleSubTypes: list || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const onVehicleTypeChange = async (e) => {
    const v = e.target.value;
    setOwn((p) => ({ ...p, vehicleTypeId: v, vehicleSubTypeId: "" }));
    if (v && v !== "Select") await fetchSubtypesByType(v);
  };

  const fetchStoresByBranch = async (vehBranchId) => {
    try {
      const res = await axios.get(`${API_BASE}/dropdowns`, {
        params: {
          branchId: vehBranchId,
          branchType,
          loginUserId,
        },
        headers: commonHeaders,
      });
      setDdl((p) => ({ ...p, stores: res.data?.stores || [] }));
    } catch (e) {
      console.error(e);
    }
  };

  const onVehicleBranchChange = async (e) => {
    const v = e.target.value;
    setOwn((p) => ({ ...p, vehicleBranchId: v, storeId: "" }));
    if (v) await fetchStoresByBranch(v);

    if (!vehicleId && v) {
      await generateBaseJobcard(v);
    }
  };

  const generateBaseJobcard = async (branchForJobcard) => {
    try {
      if (!branchForJobcard) return;
      const res = await axios.get(`${API_BASE}/jobcard/base`, {
        params: {
          branchId: branchForJobcard,
          loginUserId,
          branchType,
        },
        headers: commonHeaders,
      });
      const jobcard = res.data?.jobcard || "";
      if (jobcard) setOwn((p) => ({ ...p, jobcardNo: jobcard }));
    } catch (e) {
      console.error(e);
    }
  };

  const generateJobcardByVehicle = async () => {
    try {
      if (vehicleId) return;
      if (!own.vehicleBranchId || !own.vehicleCode || !own.vehicleName) return;

      const res = await axios.get(`${API_BASE}/jobcard/by-vehicle`, {
        params: {
          selectedBranchId: own.vehicleBranchId,
          vehicleCode: own.vehicleCode,
          vehicleName: own.vehicleName,
          loginUserId,
          branchType,
          branchId,
        },
        headers: commonHeaders,
      });

      const jobcard = res.data?.jobcard || "";
      if (jobcard) setOwn((p) => ({ ...p, jobcardNo: jobcard }));
    } catch (e) {
      console.error(e);
    }
  };

  const fetchOwnDetailsForEdit = async (id) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/view`, {
        params: { vehicleId: id, branchId, branchType, loginUserId },
        headers: commonHeaders,
      });
      const d = res.data?.vehicle || null;

      if (!d) {
        toast.warn("No vehicle data found");
        return;
      }

      setOwn((p) => ({
        ...p,
        entryDate: toISODateInput(d.fld_entrydate || d.entryDate),
        vehicleBranchId: String(d.fld_branchid ?? d.vehicleBranchId ?? ""),
        storeId: String(d.fld_storeid ?? d.storeId ?? ""),
        locationId: String(d.fld_locationid ?? d.locationId ?? ""),

        jobcardNo: d.fld_jobcardno ?? d.jobcardNo ?? "",
        hypothecationWith: d.fld_Hypothecat_with ?? d.hypothecationWith ?? "",

        vehicleTypeId: String(d.fld_vehicletypeid ?? d.vehicleTypeId ?? ""),
        vehicleSubTypeId: String(d.fld_vehicle_subtypeid ?? d.vehicleSubTypeId ?? ""),
        vehicleBasisId: String(d.fld_vehicle_basis ?? d.vehicleBasisId ?? ""),

        vehicleCode: d.fld_vehicle_code ?? d.vehicleCode ?? "",
        vehicleName: d.fld_vehicle_name ?? d.vehicleName ?? "",
        vehicleMake: d.fld_vehiclemake ?? d.vehicleMake ?? "",
        vehicleRegNo: d.fld_vehicle_Regno ?? d.vehicleRegNo ?? "",
        vehicleRegDate: toISODateInput(d.fld_vehiclereg_date ?? d.vehicleRegDate),
        model: d.fld_vehicle_model ?? d.model ?? "",
        vehicleUsageCost: d.fld_usage_cost ?? d.vehicleUsageCost ?? "",
        vehicleDescription: d.fld_vehicle_description ?? d.vehicleDescription ?? "",
        vehicleConditionId: String(d.fld_vehicle_condition ?? d.vehicleConditionId ?? ""),
        utilisationId: String(d.fld_utilisation ?? d.utilisationId ?? ""),
        remarks: d.fld_remarks ?? d.remarks ?? "",
        fuelTypeId: String(d.fld_fueltypeid ?? d.fuelTypeId ?? ""),

        mainPowerCost: d.fld_powercost ?? d.mainPowerCost ?? "",
        fuelTankCapacity: d.fld_vehfueltankcapacity ?? d.fuelTankCapacity ?? "",

        invoiceNo: d.fld_invoiceno ?? d.invoiceNo ?? "",
        chassisNo: d.fld_Chassisno ?? d.chassisNo ?? "",
        engineSLNo: d.fld_engineSLNO ?? d.engineSLNo ?? "",
        equipmentSLNo: d.fld_EquipmentSLNO ?? d.equipmentSLNo ?? "",
        purchasedFrom: d.fld_purchasedfrom ?? d.purchasedFrom ?? "",
        purchasedDate: toISODateInput(d.fld_purchaseddate ?? d.purchasedDate),
        purchasedAmount: d.fld_purchased_amt ?? d.purchasedAmount ?? "",

        insuranceNo: d.fld_insuranceno ?? d.insuranceNo ?? "",
        insuranceDate: toISODateInput(d.fld_insurance_date ?? d.insuranceDate),
        insuranceAmount: d.fld_insurance_amt ?? d.insuranceAmount ?? "",
        insuranceRenewalDate: toISODateInput(
          d.fld_insurance_renewal_date ?? d.insuranceRenewalDate
        ),
        insurancePremiumPerYear:
          d.fld_insurancepreminum_peryear ?? d.insurancePremiumPerYear ?? "",
        insuranceCompany: d.fld_insurance_company ?? d.insuranceCompany ?? "",

        fcDate: toISODateInput(d.fld_fcdate ?? d.fcDate),
        fcRenewalDate: toISODateInput(d.fld_fc_renewal_date ?? d.fcRenewalDate),

        roadTaxFromDate: toISODateInput(d.fld_roadtaxfrom_date ?? d.roadTaxFromDate),
        roadTaxToDate: toISODateInput(d.fld_roadtaxto_date ?? d.roadTaxToDate),
        permitDate: toISODateInput(d.fld_permit_date ?? d.permitDate),
        permitExpiryDate: toISODateInput(d.fld_pemitexpire_date ?? d.permitExpiryDate),
        rcBookNo: d.fld_rc_bookno ?? d.rcBookNo ?? "",
        localPermitFromDate: toISODateInput(d.fld_localpermit_date ?? d.localPermitFromDate),
        localPermitExpireDate: toISODateInput(
          d.fld_localpermitexpire_date ?? d.localPermitExpireDate
        ),
        trPermitFromDate: toISODateInput(d.fld_TRpermit_date ?? d.trPermitFromDate),
        trPermitExpireDate: toISODateInput(
          d.fld_TRpermitexpire_date ?? d.trPermitExpireDate
        ),
        freeServiceDate: toISODateInput(d.fld_freeservicedate ?? d.freeServiceDate),

        averageValue: d.fld_average ?? d.averageValue ?? "",
        averageType: d.fld_averagekmhr ?? d.averageType ?? "",

        hubFittingKm: String(d.rd_hsdfillingkm ?? d.hubFittingKm ?? "1"),
        hubFittingHr: String(d.rd_hsdfillinghr ?? d.hubFittingHr ?? "1"),

        cumHmr: d.fld_cumHMR ?? d.cumHmr ?? "",
        lastHmr: d.fld_lastupdatedHMR ?? d.lastHmr ?? "",
        cumKms: d.fld_cumKMR ?? d.cumKms ?? "",
        lastKms: d.fld_lastupdatedKMR ?? d.lastKms ?? "",
        adjHmr: d.fld_adjustmentHMR ?? d.adjHmr ?? "",
        adjKms: d.fld_adjustmentKMR ?? d.adjKms ?? "",
      }));

      setFormType("own");

      const selectedType = String(d.fld_vehicletypeid ?? "");
      if (selectedType) await fetchSubtypesByType(selectedType);

      const b = String(d.fld_branchid ?? "");
      if (b) await fetchStoresByBranch(b);
    } catch (err) {
      console.error(err);
      toast.error("Failed to load vehicle details");
    } finally {
      setLoading(false);
    }
  };

  const fetchSparesForEdit = async (id) => {
    try {
      setLoading(true);

      const res = await axios.get(`${API_BASE}/spares/view`, {
        params: { spareCatId: id, branchId, branchType, loginUserId },
        headers: commonHeaders,
      });

      const d = res.data?.spares || null;
      if (!d) {
        toast.warn("No spares data found");
        return;
      }

      setSpares((p) => ({
        ...p,
        vehicleMainTypeId: String(d.fld_vehiclemaintypeid ?? ""),
        vehicleCode: d.fld_sparesvehiclecode ?? "",

        makeId: "",
        makeName: d.fld_make ?? "",
        modelId: "",
        modelName: d.fld_model ?? "",

        itemId: String(d.fld_sparesitemid ?? ""),
        itemName: "",
        supplierId: String(d.fld_sparessupplierid ?? ""),
        supplierName: "",

        slNo: d.fld_slno ?? "",
        purchaseDate: toISODateInput(d.fld_Date ?? d.fld_purchasedate),
        expiryDate: toISODateInput(d.fld_expDate ?? d.fld_expirydate),
        purchaseValue: d.fld_purchasevalue ?? "",
        locationId: String(d.fld_location ?? ""),
        remarks: d.fld_remarks ?? "",

        uploadDoc: null,

        isNewMake: false,
        isNewModel: false,
        isNewItem: false,
        isNewSupplier: false,
      }));

      setFormType("spares");
    } catch (err) {
      console.error(err);
      toast.error("Failed to load spares details");
    } finally {
      setLoading(false);
    }
  };

  const setHubKm = (yes) => setOwn((p) => ({ ...p, hubFittingKm: yes ? "1" : "0" }));
  const setHubHr = (yes) => setOwn((p) => ({ ...p, hubFittingHr: yes ? "1" : "0" }));
  const setAverageType = (type) => setOwn((p) => ({ ...p, averageType: type }));

  const handleSubmit = async () => {
    if (loading) return;

    try {
      setLoading(true);

      if (formType === "own") {
        if (!own.vehicleTypeId) return toast.error("Select Vehicle Type");
        if (!own.vehicleCode) return toast.error("Enter Vehicle Code");
        if (!own.vehicleName) return toast.error("Enter Vehicle Name");
        if (!own.vehicleBasisId) return toast.error("Select Vehicle Basis");
        if (!own.fuelTypeId) return toast.error("Select Fuel Type");
        if (!own.locationId) return toast.error("Select Location");

        const uploaded = {
          insurance: [],
          fitness: [],
          roadtax: [],
          permit: [],
          rccopy: [],
          invoice: [],
          emission: [],
        };

        const insuranceFn = await uploadSingle("InsuranceDocument", docs.insurance);
        if (insuranceFn) uploaded.insurance.push(insuranceFn);

        const fitnessFn = await uploadSingle("FitnessDocument", docs.fitness);
        if (fitnessFn) uploaded.fitness.push(fitnessFn);

        const roadTaxFn = await uploadSingle("RoadTaxDocument", docs.roadTax);
        if (roadTaxFn) uploaded.roadtax.push(roadTaxFn);

        const permitFn = await uploadSingle("PermitDocument", docs.permit);
        if (permitFn) uploaded.permit.push(permitFn);

        const rcCopyFn = await uploadSingle("RcCopyDocument", docs.rcCopy);
        if (rcCopyFn) uploaded.rccopy.push(rcCopyFn);

        const invoiceFn = await uploadSingle("InvoiceDocument", docs.invoice);
        if (invoiceFn) uploaded.invoice.push(invoiceFn);

        const payload = {
          mode: vehicleId ? "update" : "insert",
          vehicleId,

          branchId,
          branchType,
          loginUserId,
          ipAddress: "",

          entryDate: own.entryDate,

          vehicleBranchId: own.vehicleBranchId || branchId,
          storeId: own.storeId || 0,
          locationId: own.locationId,

          jobcardNo: own.jobcardNo || "",

          vehicleEquipmentTypeId: own.vehicleTypeId,
          vehicleSubTypeId: own.vehicleSubTypeId || "Select",
          vehicleBasisId: own.vehicleBasisId,

          vehicleBasisText: "",

          vehicleCode: own.vehicleCode,
          vehicleName: own.vehicleName,
          vehicleMake: own.vehicleMake,
          vehicleRegNo: own.vehicleRegNo,
          vehicleRegDate: own.vehicleRegDate,

          model: own.model,
          vehicleDesc: own.vehicleDescription,

          vehicleConditionId: own.vehicleConditionId || 0,
          utilisationId: own.utilisationId || 0,
          remarks: own.remarks || "",

          fuelTypeId: own.fuelTypeId,
          usageCost: own.vehicleUsageCost || 0,

          manpowerCost: own.mainPowerCost || 0,
          powerCost: own.mainPowerCost || 0,
          fuelTankCapacity: own.fuelTankCapacity || "",

          invoiceNo: own.invoiceNo || "",
          chassisNo: own.chassisNo || "",
          engineSlNo: own.engineSLNo || "",
          equipmentSlNo: own.equipmentSLNo || "",

          purchasedFrom: own.purchasedFrom || "",
          purchasedDate: own.purchasedDate || "",
          purchasedAmt: own.purchasedAmount || 0,

          hypothecation: own.hypothecationWith || "",

          insuranceNo: own.insuranceNo || "",
          insuranceDate: own.insuranceDate || "",
          insuranceAmt: own.insuranceAmount || 0,
          insuranceRenewalDate: own.insuranceRenewalDate || "",
          insurancePremiumPerYear: own.insurancePremiumPerYear || "",
          insuranceCompany: own.insuranceCompany || "",

          fcDate: own.fcDate || "",
          fcRenewalDate: own.fcRenewalDate || "",

          roadTaxFromDate: own.roadTaxFromDate || "",
          roadTaxToDate: own.roadTaxToDate || "",

          permitDate: own.permitDate || "",
          permitExpDate: own.permitExpiryDate || "",

          localPermitDate: own.localPermitFromDate || "",
          localPermitExpDate: own.localPermitExpireDate || "",
          trPermitDate: own.trPermitFromDate || "",
          trPermitExpDate: own.trPermitExpireDate || "",

          rcBookNo: own.rcBookNo || "",
          freeServiceDate: own.freeServiceDate || "",

          average: own.averageValue || "",
          averageUnit: own.averageType === "HR" ? "HR" : "KM",

          hsdKm: own.hubFittingKm === "1" ? 1 : 0,
          hsdHr: own.hubFittingHr === "1" ? 1 : 0,

          cumulativeHMR: own.cumHmr || "0",
          lastHMR: own.lastHmr || "0",
          cumulativeKMR: own.cumKms || "0",
          lastKMR: own.lastKms || "0",
          adjustHMR: own.adjHmr || "0",
          adjustKMR: own.adjKms || "0",

          docs: uploaded,
        };

        const res = await axios.post(`${API_BASE}/save`, payload, {
          headers: commonHeaders,
        });

        toast.success(`Own Vehicle ${res.data?.mode || ""} saved successfully`);

        if (!vehicleId && res.data?.vehicleId) {
          navigate(`/VehicleV2/OwnVehicleDetailsEntry?vehicleId=${res.data.vehicleId}`);
        }
      }

      if (formType === "spares") {
        if (!spares.vehicleMainTypeId) return toast.error("Select Vehicle Main Type");
        if (!spares.vehicleCode) return toast.error("Enter Vehicle Code");
        if (!spares.purchaseDate) return toast.error("Select Purchase Date");

        const sparesDocName = await uploadSingle("SparesDocument", spares.uploadDoc);

        const sparesPayload = {
          branchId,
          branchType,
          loginUserId,

          spareCatId: spareId ? String(spareId) : "",
          vehMainTypeId: spares.vehicleMainTypeId,
          vehicleCode: spares.vehicleCode,

          itemId: spares.isNewItem ? 0 : spares.itemId,
          itemNewName: spares.isNewItem ? spares.itemName : "",

          supplierId: spares.isNewSupplier ? 0 : spares.supplierId,
          supplierNewName: spares.isNewSupplier ? spares.supplierName : "",

          makeId: spares.isNewMake ? 0 : spares.makeId,
          makeNewName: spares.isNewMake ? spares.makeName : "",

          modelId: spares.isNewModel ? 0 : spares.modelId,
          modelNewName: spares.isNewModel ? spares.modelName : "",

          slno: spares.slNo || "",
          purchaseDate: spares.purchaseDate,
          expiryDate: spares.expiryDate,
          purchaseValue: spares.purchaseValue || "",
          locationId: spares.locationId || 0,
          remarks: spares.remarks || "",

          loginUserId,
          documentFilename: sparesDocName || "",
        };

        const res = await axios.post(`${API_BASE}/spares/save`, sparesPayload, {
          headers: commonHeaders,
        });
        toast.success(`Spares ${res.data?.mode || ""} saved successfully`);
      }
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.message || "Submit failed");
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    if (formType === "own") {
      setOwn((p) => ({
        ...p,
        jobcardNo: "",
        hypothecationWith: "",
        vehicleTypeId: "",
        vehicleSubTypeId: "",
        vehicleBasisId: "",
        vehicleCode: "",
        vehicleName: "",
        vehicleMake: "",
        vehicleRegNo: "",
        vehicleRegDate: "",
        model: "",
        vehicleUsageCost: "",
        vehicleDescription: "",
        vehicleConditionId: "",
        utilisationId: "",
        remarks: "",
        fuelTypeId: "",
        mainPowerCost: "",
        fuelTankCapacity: "",
        invoiceNo: "",
        chassisNo: "",
        engineSLNo: "",
        equipmentSLNo: "",
        purchasedFrom: "",
        purchasedDate: "",
        purchasedAmount: "",
        insuranceNo: "",
        insuranceDate: "",
        insuranceAmount: "",
        insuranceRenewalDate: "",
        insurancePremiumPerYear: "",
        insuranceCompany: "",
        fcDate: "",
        fcRenewalDate: "",
        roadTaxFromDate: "",
        roadTaxToDate: "",
        permitDate: "",
        permitExpiryDate: "",
        rcBookNo: "",
        localPermitFromDate: "",
        localPermitExpireDate: "",
        trPermitFromDate: "",
        trPermitExpireDate: "",
        freeServiceDate: "",
        averageValue: "",
        averageType: "",
        cumHmr: "",
        lastHmr: "",
        cumKms: "",
        lastKms: "",
        adjHmr: "",
        adjKms: "",
      }));

      setDocs({
        insurance: null,
        fitness: null,
        roadTax: null,
        permit: null,
        rcCopy: null,
        invoice: null,
        endorsement: null,
      });

      generateBaseJobcard(own.vehicleBranchId || branchId);
    } else {
      setSpares((p) => ({
        ...p,
        vehicleMainTypeId: "",
        vehicleCode: "",
        makeId: "",
        makeName: "",
        modelId: "",
        modelName: "",
        itemId: "",
        itemName: "",
        supplierId: "",
        supplierName: "",
        slNo: "",
        purchaseDate: "",
        expiryDate: "",
        purchaseValue: "",
        locationId: "",
        remarks: "",
        uploadDoc: null,
        isNewMake: false,
        isNewModel: false,
        isNewItem: false,
        isNewSupplier: false,
      }));
    }
  };

  useEffect(() => {
    fetchDropdowns(branchId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!vehicleId && !spareId) return;
    if (vehicleId) fetchOwnDetailsForEdit(vehicleId);
    if (spareId) fetchSparesForEdit(spareId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, spareId]);

  useEffect(() => {
    if (vehicleId) return;
    const b = own.vehicleBranchId || branchId;
    if (!own.jobcardNo && b) generateBaseJobcard(b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicleId, own.vehicleBranchId]);

  useEffect(() => {
    generateJobcardByVehicle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [own.vehicleCode, own.vehicleName, own.vehicleBranchId]);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">VEHICLE DETAILS ENTRY</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <ToastContainer />

        <div className={sectionClass}>
          <div className="mb-6 flex gap-6">
            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="formType"
                value="own"
                checked={formType === "own"}
                onChange={() => setFormType("own")}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                Own Vehicle
              </span>
            </label>

            <label className="flex items-center gap-2">
              <input
                type="radio"
                name="formType"
                value="spares"
                checked={formType === "spares"}
                onChange={() => setFormType("spares")}
                className="text-blue-600 focus:ring-blue-500"
              />
              <span className={isDark ? "text-gray-300" : "text-gray-700"}>
                Spares And Catalogue
              </span>
            </label>
          </div>

          {formType === "own" && (
            <div className="space-y-6">
              <Section title="Own Vehicle Details Entry" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input
                    label="Entry Date"
                    type="date"
                    name="entryDate"
                    value={own.entryDate}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Vehicle Branch"
                    name="vehicleBranchId"
                    value={own.vehicleBranchId}
                    onChange={onVehicleBranchChange}
                    options={ddl.vehicleBranches}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Jobcard No"
                    name="jobcardNo"
                    value={own.jobcardNo}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Hypothecation With"
                    name="hypothecationWith"
                    value={own.hypothecationWith}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                </div>
              </Section>

              <Section title="Vehicle General Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <Select
                    label="Vehicle Type"
                    name="vehicleTypeId"
                    value={own.vehicleTypeId}
                    onChange={onVehicleTypeChange}
                    options={ddl.vehicleEquipmentTypes}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Vehicle Subtype"
                    name="vehicleSubTypeId"
                    value={own.vehicleSubTypeId}
                    onChange={handleOwnChange}
                    options={ddl.vehicleSubTypes}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Code"
                    name="vehicleCode"
                    value={own.vehicleCode}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Name"
                    name="vehicleName"
                    value={own.vehicleName}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Make"
                    name="vehicleMake"
                    value={own.vehicleMake}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Reg No"
                    name="vehicleRegNo"
                    value={own.vehicleRegNo}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Usage Cost"
                    name="vehicleUsageCost"
                    value={own.vehicleUsageCost}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Reg Date"
                    type="date"
                    name="vehicleRegDate"
                    value={own.vehicleRegDate}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Model"
                    name="model"
                    value={own.model}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Current Cumulative HMR"
                    name="cumHmr"
                    value={own.cumHmr}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                  <Input
                    label="Last Updated HMR"
                    name="lastHmr"
                    value={own.lastHmr}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                  <Input
                    label="Current Cumulative KMS"
                    name="cumKms"
                    value={own.cumKms}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                  <Input
                    label="Last Updated KMS"
                    name="lastKms"
                    value={own.lastKms}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                  <Input
                    label="Adjustment HMR"
                    name="adjHmr"
                    value={own.adjHmr}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />
                  <Input
                    label="Adjustment KMS"
                    name="adjKms"
                    value={own.adjKms}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Vehicle Basis"
                    name="vehicleBasisId"
                    value={own.vehicleBasisId}
                    onChange={handleOwnChange}
                    options={ddl.vehicleBasis}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Store"
                    name="storeId"
                    value={own.storeId}
                    onChange={handleOwnChange}
                    options={ddl.stores}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Main Power Cost"
                    name="mainPowerCost"
                    value={own.mainPowerCost}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Location"
                    name="locationId"
                    value={own.locationId}
                    onChange={handleOwnChange}
                    options={ddl.locations}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Input
                    label="Vehicle Description"
                    name="vehicleDescription"
                    value={own.vehicleDescription}
                    onChange={handleOwnChange}
                    inputClass={inputClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Vehicle Condition"
                    name="vehicleConditionId"
                    value={own.vehicleConditionId}
                    onChange={handleOwnChange}
                    options={ddl.vehicleConditions}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Select
                    label="Utilisation"
                    name="utilisationId"
                    value={own.utilisationId}
                    onChange={handleOwnChange}
                    options={ddl.utilisations}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />
                </div>
              </Section>

              <Section title="Insurance Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input label="Insurance Number" name="insuranceNo" value={own.insuranceNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Insurance Date" type="date" name="insuranceDate" value={own.insuranceDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Insurance Amount" name="insuranceAmount" value={own.insuranceAmount} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Insurance Renewal Date" type="date" name="insuranceRenewalDate" value={own.insuranceRenewalDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Insurance Premium Per Year" name="insurancePremiumPerYear" value={own.insurancePremiumPerYear} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Insurance Company" name="insuranceCompany" value={own.insuranceCompany} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Vehicle Parts Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input label="Coach Number" name="chassisNo" value={own.chassisNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Engine Size" name="engineSLNo" value={own.engineSLNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="First Service Date" type="date" name="freeServiceDate" value={own.freeServiceDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />

                  <Select
                    label="Fuel Type"
                    name="fuelTypeId"
                    value={own.fuelTypeId}
                    onChange={handleOwnChange}
                    options={ddl.fuelTypes}
                    selectClass={selectClass}
                    labelClass={labelClass}
                  />

                  <Input label="Equipment S/No" name="equipmentSLNo" value={own.equipmentSLNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Fuel Tank Capacity" name="fuelTankCapacity" value={own.fuelTankCapacity} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="FC Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Input label="FC Date" type="date" name="fcDate" value={own.fcDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="FC Renewal Date" type="date" name="fcRenewalDate" value={own.fcRenewalDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Consignee / Purchase Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input label="Purchased From" name="purchasedFrom" value={own.purchasedFrom} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Purchased Date" type="date" name="purchasedDate" value={own.purchasedDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Purchased Amount" name="purchasedAmount" value={own.purchasedAmount} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Invoice Number" name="invoiceNo" value={own.invoiceNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />

                  <div>
                    <label className={labelClass}>Hub Fitting KM</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="hubKm"
                          checked={own.hubFittingKm === "1"}
                          onChange={() => setHubKm(true)}
                          className="text-blue-600"
                        />
                        <span className={isDark ? "text-gray-300" : "text-gray-700"}>Yes</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="hubKm"
                          checked={own.hubFittingKm === "0"}
                          onChange={() => setHubKm(false)}
                          className="text-blue-600"
                        />
                        <span className={isDark ? "text-gray-300" : "text-gray-700"}>No</span>
                      </label>
                    </div>
                  </div>

                  <div>
                    <label className={labelClass}>Average Type</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="avgType"
                          checked={own.averageType === "KM"}
                          onChange={() => setAverageType("KM")}
                          className="text-blue-600"
                        />
                        <span className={isDark ? "text-gray-300" : "text-gray-700"}>KM</span>
                      </label>
                      <label className="flex items-center gap-1">
                        <input
                          type="radio"
                          name="avgType"
                          checked={own.averageType === "HR"}
                          onChange={() => setAverageType("HR")}
                          className="text-blue-600"
                        />
                        <span className={isDark ? "text-gray-300" : "text-gray-700"}>HMR</span>
                      </label>
                    </div>
                  </div>

                  <Input label="Average Value" name="averageValue" value={own.averageValue} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Tax Details" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <Input label="Road Tax From Date" type="date" name="roadTaxFromDate" value={own.roadTaxFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Road Tax To Date" type="date" name="roadTaxToDate" value={own.roadTaxToDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Permit Date" type="date" name="permitDate" value={own.permitDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="RC Book Number" name="rcBookNo" value={own.rcBookNo} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Permit Expiry Date" type="date" name="permitExpiryDate" value={own.permitExpiryDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Local Permit From Date" type="date" name="localPermitFromDate" value={own.localPermitFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="Local Permit Expire Date" type="date" name="localPermitExpireDate" value={own.localPermitExpireDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="TR Permit From Date" type="date" name="trPermitFromDate" value={own.trPermitFromDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                  <Input label="TR Permit Expire Date" type="date" name="trPermitExpireDate" value={own.trPermitExpireDate} onChange={handleOwnChange} inputClass={inputClass} labelClass={labelClass} />
                </div>
              </Section>

              <Section title="Scanned Documents" isDark={isDark}>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FileInput label="Insurance Document" onChange={handleDocChange("insurance")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Fitness Document" onChange={handleDocChange("fitness")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Road Tax Document" onChange={handleDocChange("roadTax")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Permit Document" onChange={handleDocChange("permit")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="RC Copy Document" onChange={handleDocChange("rcCopy")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Invoice Document" onChange={handleDocChange("invoice")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                  <FileInput label="Endorsement Document" onChange={handleDocChange("endorsement")} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
                </div>
              </Section>

              <div className="flex justify-end gap-4 mt-6">
                <button
                  className={`px-6 py-2 rounded-lg shadow transition ${
                    isDark
                      ? "bg-gray-700 text-white hover:bg-gray-600"
                      : "bg-gray-300 text-gray-800 hover:bg-gray-400"
                  }`}
                  onClick={handleClear}
                  disabled={loading}
                >
                  Clear
                </button>
                <button
                  className={`px-6 py-2 text-white rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Please wait..." : "Submit"}
                </button>
              </div>
            </div>
          )}

          {formType === "spares" && (
            <Section title="Spares And Catalogue" isDark={isDark}>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                <Select
                  label="Vehicle Main Type"
                  name="vehicleMainTypeId"
                  value={spares.vehicleMainTypeId}
                  onChange={handleSparesChange}
                  options={ddl.vehMainTypes}
                  selectClass={selectClass}
                  labelClass={labelClass}
                />

                <Input label="Veh Code" name="vehicleCode" value={spares.vehicleCode} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />

                <div className="flex items-start gap-2">
                  {spares.isNewMake ? (
                    <Input label="Make" name="makeName" value={spares.makeName} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                  ) : (
                    <Select label="Make" name="makeId" value={spares.makeId} onChange={handleSparesChange} options={ddl.sparesMakes} selectClass={selectClass} labelClass={labelClass} />
                  )}
                  <div className="flex items-center mt-7">
                    <input type="checkbox" name="isNewMake" checked={spares.isNewMake} onChange={handleSparesChange} className="text-blue-600" />
                    <span className={`ml-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>New</span>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  {spares.isNewModel ? (
                    <Input label="Model" name="modelName" value={spares.modelName} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                  ) : (
                    <Select label="Model" name="modelId" value={spares.modelId} onChange={handleSparesChange} options={ddl.sparesModels} selectClass={selectClass} labelClass={labelClass} />
                  )}
                  <div className="flex items-center mt-7">
                    <input type="checkbox" name="isNewModel" checked={spares.isNewModel} onChange={handleSparesChange} className="text-blue-600" />
                    <span className={`ml-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>New</span>
                  </div>
                </div>

                <div className="flex items-start gap-2">
                  {spares.isNewItem ? (
                    <Input label="Description" name="itemName" value={spares.itemName} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                  ) : (
                    <Select label="Description" name="itemId" value={spares.itemId} onChange={handleSparesChange} options={ddl.sparesItems} selectClass={selectClass} labelClass={labelClass} />
                  )}
                  <div className="flex items-center mt-7">
                    <input type="checkbox" name="isNewItem" checked={spares.isNewItem} onChange={handleSparesChange} className="text-blue-600" />
                    <span className={`ml-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>New</span>
                  </div>
                </div>

                <Input label="SL. NO" name="slNo" value={spares.slNo} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <Input label="Pur Date" type="date" name="purchaseDate" value={spares.purchaseDate} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <Input label="Expiry Date" type="date" name="expiryDate" value={spares.expiryDate} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                <Input label="Pur Value" name="purchaseValue" value={spares.purchaseValue} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />

                <div className="flex items-start gap-2">
                  {spares.isNewSupplier ? (
                    <Input label="Pur From" name="supplierName" value={spares.supplierName} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />
                  ) : (
                    <Select label="Pur From" name="supplierId" value={spares.supplierId} onChange={handleSparesChange} options={ddl.sparesSuppliers} selectClass={selectClass} labelClass={labelClass} />
                  )}
                  <div className="flex items-center mt-7">
                    <input type="checkbox" name="isNewSupplier" checked={spares.isNewSupplier} onChange={handleSparesChange} className="text-blue-600" />
                    <span className={`ml-1 ${isDark ? "text-gray-300" : "text-gray-700"}`}>New</span>
                  </div>
                </div>

                <Select
                  label="Location"
                  name="locationId"
                  value={spares.locationId}
                  onChange={handleSparesChange}
                  options={ddl.sparesLocations}
                  selectClass={selectClass}
                  labelClass={labelClass}
                />

                <Input label="Remarks" name="remarks" value={spares.remarks} onChange={handleSparesChange} inputClass={inputClass} labelClass={labelClass} />

                <FileInput label="Upload Document" onChange={handleSparesDocChange} inputClass={inputClass} labelClass={labelClass} isDark={isDark} />
              </div>

              <div className="flex justify-end mt-6">
                <button
                  className={`px-6 py-2 text-white rounded-lg shadow transition ${buttonClass} ${hoverClass}`}
                  onClick={handleSubmit}
                  disabled={loading}
                >
                  {loading ? "Please wait..." : "Submit"}
                </button>
              </div>
            </Section>
          )}
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

function Section({ title, children, isDark }) {
  return (
    <div
      className={`mb-6 p-6 rounded-2xl shadow-xl border ${
        isDark
          ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
          : "bg-white border-gray-200 text-black"
      }`}
    >
      <h2 className={`text-xl font-semibold mb-4 ${isDark ? "text-white" : "text-gray-800"}`}>
        {title}
      </h2>
      {children}
    </div>
  );
}

function Input({ label, type = "text", name, value, onChange, inputClass, labelClass }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <input type={type} name={name} value={value ?? ""} onChange={onChange} className={inputClass} />
    </div>
  );
}

function Select({ label, name, value, onChange, options, selectClass, labelClass }) {
  const isObjectOptions =
    Array.isArray(options) && options.length > 0 && typeof options[0] === "object";

  return (
    <div>
      <label className={labelClass}>{label}</label>
      <select name={name} value={value ?? ""} onChange={onChange} className={selectClass}>
        <option value="">Select</option>
        {isObjectOptions
          ? options.map((opt) => (
              <option key={opt.id} value={String(opt.id)}>
                {opt.name}
              </option>
            ))
          : (options || []).map((opt, idx) => (
              <option key={idx} value={String(opt)}>
                {opt}
              </option>
            ))}
      </select>
    </div>
  );
}

function FileInput({ label, onChange, inputClass, labelClass, isDark }) {
  return (
    <div>
      <label className={labelClass}>{label}</label>
      <div className="flex items-center">
        <input
          type="file"
          onChange={onChange}
          className={`${inputClass} file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 ${
            isDark ? "file:bg-gray-700 file:text-white" : "file:bg-gray-200 file:text-gray-700"
          } file:text-sm file:font-semibold`}
        />
      </div>
    </div>
  );
}

export default OwnVehicleDetailsEntry;